/* Copyright (c) 2017 Backpack Health LLC */

/* eslint-disable camelcase */
/* global window */

import { apiRequest, authorizedApiRequest } from './ApiRequest';
import { localstore, authToken, sessionstore } from '../utilities/LocalStorage';
import AppUtils from '../utilities/AppUtils';
import ApiErrorModel from '../models/ApiErrorModel';
import SessionModel from '../models/SessionModel';

const SessionApi = {
  create: function createSession(credentials) {
    const encodedCredentials = window.btoa(
      `${credentials.email}:${credentials.password}`
    );

    return apiRequest('post', 'sessions').set(
      'Authorization',
      `Basic ${encodedCredentials}`
    );
  },
  verifyCredentials: function verifyCredentials(credentials) {
    const encodedCredentials = window.btoa(
      `${credentials.email}:${credentials.password}`
    );

    return apiRequest('head', 'sessions').set(
      'Authorization',
      `Basic ${encodedCredentials}`
    );
  },
  mfaLogin: function createSession() {
    return authorizedApiRequest('post', 'sessions/mfa/login');
  },
  get: function getSession(token) {
    return apiRequest('get', `sessions/${token}`);
  },
  del: function deleteSession(token) {
    return apiRequest('delete', `sessions/${token}`);
  },
  refresh: function refreshToken() {
    return authorizedApiRequest('post', 'sessions/refresh');
  },
  logout: function logout() {
    const token = authToken.get();
    authToken.remove();
    return this.del(token);
  },
  sendMfaCode(token) {
    return apiRequest('post', 'sessions/mfa/code').set(
      'Authorization',
      `Bearer ${token}`
    );
  },
  enableMfa() {
    return authorizedApiRequest('put', 'sessions/mfa/enable');
  },
  mfaForgotPassword(token) {
    return apiRequest('post', 'sessions/mfa/forgotPassword').set(
      'Authorization',
      `Bearer ${token}`
    );
  },
  changePassword(credentials) {
    const encodedCredentials = window.btoa(
      `${credentials.email}:${credentials.password}`
    );
    return apiRequest('post', 'sessions/changePassword').set(
      'Authorization',
      `Basic ${encodedCredentials}`
    );
  },
  mfaChangePassword(credentials) {
    const encodedCredentials = window.btoa(
      `${credentials.email}:${credentials.password}`
    );
    return apiRequest('post', 'sessions/mfa/changePassword').set(
      'Authorization',
      `Basic ${encodedCredentials}`
    );
  },
  changeEmail(token) {
    return authorizedApiRequest('post', 'sessions/changeEmail').set(
      'Authorization',
      `Bearer ${token}`
    );
  },
  mfaChangeEmail(token) {
    return apiRequest('post', 'sessions/mfa/changeEmail').set(
      'Authorization',
      `Bearer ${token}`
    );
  },
};

const SessionService = {
  login(credentialsData, callback) {
    return SessionApi.create({
      email: credentialsData.email,
      password: credentialsData.password,
    })
      .send({
        duration: credentialsData.duration,
      })
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        if (!err) {
          authToken.set(res.body.data.access_token);
        }
        callback(apiError, res.body.data);
      });
  },

  verifyCredentials(credentialsData, callback) {
    return SessionApi.verifyCredentials({
      email: credentialsData.email,
      password: credentialsData.password,
    })
      .end((err) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError, null);
      });
  },

  mfaLogin(mfaCode, duration, callback) {
    return SessionApi.mfaLogin()
      .send({
        mfa_code: mfaCode,
        duration,
      })
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        if (!err) {
          authToken.set(res.body.data.access_token);
        }
        callback(apiError, null);
      });
  },

  mfaForgotPassword(mfaCode, password, token, duration, callback) {
    return SessionApi.mfaForgotPassword(token)
      .send({
        mfa_code: mfaCode,
        password,
        duration,
      })
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        if (!err) {
          authToken.set(res.body.data.access_token);
        }
        callback(apiError, null);
      });
  },

  get(token, callback) {
    return SessionApi.get(token).end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      const session = res ? new SessionModel(res.body.data) : {};
      callback(apiError, session);
    });
  },

  refresh(callback) {
    return SessionApi.refresh().end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      let session = {};
      if (apiError) {
        localstore.clear();
        sessionstore.clear();
        session = new SessionModel({});
      } else {
        authToken.set(res.body.data.access_token);
        const tokenData = AppUtils.extractDataFromAuthToken(res.body.data.access_token);
        session = new SessionModel(tokenData);
      }

      callback(apiError, session);
    });
  },

  logout(callback) {
    return SessionApi.logout().end(err => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, null);
    });
  },

  sendMfaCode(token, callback) {
    return SessionApi.sendMfaCode(token).end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, res.body.data);
    });
  },

  enableMfa(mfaCode, callback) {
    return SessionApi.enableMfa()
      .send({
        mfa_code: mfaCode,
      })
      .end(err => {
        const apiError = err ? new ApiErrorModel(err) : null;
        callback(apiError, null);
      });
  },

  changePassword(credentials, password, duration, callback) {
    return SessionApi.changePassword(credentials)
      .send({
        password,
        duration,
      })
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        if (!err) {
          authToken.set(res.body.data.access_token);
        }
        callback(apiError, null);
      });
  },

  mfaChangePassword(credentials, mfa_code, password, duration, callback) {
    return SessionApi.mfaChangePassword(credentials)
      .send({
        mfa_code,
        password,
        duration,
      })
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        if (!err) {
          authToken.set(res.body.data.access_token);
        }
        callback(apiError, null);
      });
  },

  changeEmail(token, password, duration, callback) {
    return SessionApi.changeEmail(token)
      .send({
        password,
        duration,
      })
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        if (!err) {
          authToken.set(res.body.data.access_token);
        }
        callback(apiError, null);
      });
  },

  mfaChangeEmail(mfa_code, password, token, duration, callback) {
    return SessionApi.mfaChangeEmail(token)
      .send({
        mfa_code,
        password,
        duration,
      })
      .end((err, res) => {
        const apiError = err ? new ApiErrorModel(err) : null;
        if (!err) {
          authToken.set(res.body.data.access_token);
        }
        callback(apiError, null);
      });
  },
};

export default SessionService;
